import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import {
  addWorkOrderTrip,
  checkAllIssuesMarked,
  convertMaterialTypeToFormType,
  getActiveWorkOrderQuote,
  getQuoteStatus,
  getWorkOrderIssueStatus,
  getWorkOrderIssueTitle,
  getWorkOrderStatus,
  isQuoteRejected,
  resolveIssueMaterials,
  updateWorkOrderIssue,
  updateWorkOrderIssueStatus,
  updateWorkOrderTrip,
} from 'src/helper/work-order-utils';
import {
  ISSUES_STATUS,
  ISSUES_STATUS_LABEL,
  ISSUES_STATUS_LABEL_MAP,
  QUOTE_STATUS_KEYS,
  UNRESOLVE_TYPE,
  WO_STATUS_KEYS,
  WO_STATUS_LABELS,
} from 'src/views/VendorPortal/WorkOrder/constants';
import { WO_CLOSE_WORK_ORDER_TABS_KEYS } from 'src/views/VendorPortal/WorkOrder/SidePanel/CloseWorkOrder/constants';
import {
  addQuote,
  addQuoteTask,
  cancelVendorWorkOrder,
  closeWorkOrder,
  enRouteWorkOrder,
  getCategoryOptions,
  getWorkOrderDetails,
  getWorkOrders,
  markAsPending,
  markAsUnresolve,
  markIssueAsDone,
  pauseEnRouteWorkOrder,
  pauseWorkInTrip,
  removeQuoteTask,
  rescheduleVendorWorkOrder,
  resumeEnRouteWorkOrder,
  resumeWorkInTrip,
  saveServiceReportChanges,
  scheduleVendorReturnTrip,
  startWorkInTrip,
  updateDeclineReason,
  updateQuoteTask,
} from './api';
import {
  buildIssueDetailsPanelSnapshot,
  formatQuoteResponseData,
  formatQuoteTaskResponseData,
} from './helpers';
import type {
  MaterialType,
  QuoteDataResponseType,
  QuoteTaskResponseType,
  QuoteTaskType,
  QuoteType,
  StatusType,
  WorkOrderAPIResponsePayload,
  WorkOrderDataType,
  WorkOrderIssueType,
  WorkOrderListingType,
  WorkOrderStateType,
  WorkOrderStatusChangeResponse,
} from './types';

type OptionType = import('src/helper/shared-types').OptionType;

// ORI-593 (QA): the issue statuses whose panel content depends on cancellation data — a cancelled
// issue, or an unresolved one. UNRESOLVED is deliberately broader than "cancelled" (it also covers
// declines), which is correct here: this only decides whether the open panel is kept IN SYNC, never
// what renders — IssueDetails/index.tsx makes that call.
// Used on BOTH sides of the open-panel re-sync gate below.
const isCancelledLike = (value?: string) =>
  value === ISSUES_STATUS.CANCELED || value === ISSUES_STATUS.UNRESOLVED;

const extraReducers = (builder: ActionReducerMapBuilder<WorkOrderStateType>) => {
  builder.addCase(getWorkOrders().pending, (state: WorkOrderStateType, action) => {
    state.isLoading = true;
    state.currentFilterReferenceObj = action.meta.arg;
  });

  builder.addCase(
    getWorkOrders().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderAPIResponsePayload>) => {
      // The transport seam fulfils thunks with `null` on 401 (src/helper/api.ts),
      // so the payload can be nullish here even though the typed contract says
      // otherwise. Short-circuit to preserve state instead of throwing inside
      // dispatch and tearing down the vendor list.
      if (!action.payload?.data) {
        state.isFilteringList = false;
        state.isFilterLoading = false;
        state.isLoading = false;
        return;
      }

      const formattedData: WorkOrderListingType[] = action.payload.data.map((data) => ({
        abilities: data.abilities,
        address: data.property.address,
        category: {
          // work_order_issues[] is allowed to be empty by the type; fall back
          // to an empty label so a zero-issue WO row still renders.
          label: data.work_order_issues[0]?.problem.category.label ?? '',
          priority: data.priority,
        },
        completed_at: data.completed_at ?? data.canceled_at ?? null,
        due_date: data.due_date,
        has_missing_data: data.has_missing_data,
        paused_at: data.paused_at,
        resolved_at: data.resolved_at,
        status: getWorkOrderStatus(data.status, data.trips, data.work_order_issues),
        timezone: data.timezone,
        trips: data.trips,
        work_order_id: data.work_order_id,
        work_order_issues: data.work_order_issues,
        work_order_number: data.work_order_number,
        invoice_status: data?.invoice_status || null,
        total_invoice_amount_in_cents: data?.total_invoice_amount_in_cents || null,
        unread_messages_count: data?.unread_messages_count || 0,
      }));

      if (action.payload.meta.current_page > 1) {
        state.data = [...state.data, ...formattedData];
      } else {
        state.data = formattedData;
        state.isFilterLoading = false;
      }

      state.isFilteringList = false;
      state.isLoading = false;
      state.tabCounts = action.payload.meta.tab_counts;
      state.meta = action.payload.meta;
    },
  );

  builder.addCase(getWorkOrders().rejected, (state: WorkOrderStateType) => {
    state.isFilteringList = false;
    state.isFilterLoading = false;
    state.isLoading = false;
    state.data = [];
  });

  builder.addCase(
    getCategoryOptions().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<OptionType[]>) => {
      state.categoryOptions = action.payload;
      state.isFilterLoading = false;
    },
  );

  builder.addCase(getCategoryOptions().rejected, (state: WorkOrderStateType) => {
    state.isFilterLoading = false;
  });

  // Fetch Work order details
  builder.addCase(getWorkOrderDetails().pending, (state: WorkOrderStateType) => {
    state.sidePanels.details.isLoading = true;
    // ORI-627: a new attempt (initial load, Pusher refetch, or Retry) supersedes the
    // previous failure, so the error branch yields to the skeleton immediately.
    state.sidePanels.details.hasDetailsError = false;
  });
  builder.addCase(
    getWorkOrderDetails().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderDataType>) => {
      const workOrderIssues: WorkOrderIssueType[] = action.payload.work_order_issues.map(
        (issue: WorkOrderIssueType) => ({
          ...issue,
          // ORI-620: capture the bare wire title BEFORE the next line overwrites `title`
          // with the work-type suffix. Must be read from the raw payload issue here — never
          // re-derived in the scheduleVendorReturnTrip reducer below, whose input title is
          // already suffixed.
          base_title: issue.title,
          title: getWorkOrderIssueTitle(issue, action.payload.quotes),
          ...(issue.quote_task && {
            quote_task: {
              quote_task_id: issue.quote_task.quote_task_id,
              task_type: issue.quote_task.task_type,
              selected_option: {
                ...issue.quote_task.selected_option,
                materials: issue.quote_task.selected_option?.materials?.map(
                  (material: MaterialType) => convertMaterialTypeToFormType(material),
                ),
              },
            },
          }),
          status: getWorkOrderIssueStatus(issue),
          ...(issue.is_approved_in_quote
            ? { materials: issue.quote_task?.selected_option?.materials }
            : {}),
          ...(issue.issue_recommended_items && {
            recommended_tools: issue.issue_recommended_items.filter(
              (item) => item.item_type === 'tool',
            ),
            recommended_materials: issue.issue_recommended_items.filter(
              (item) => item.item_type === 'material',
            ),
          }),
        }),
      );
      const formattedData: WorkOrderDataType = {
        ...action.payload,
        quotes: action.payload.quotes.map((quote) => ({
          ...quote,
          status: getQuoteStatus(quote),
        })),
        status: getWorkOrderStatus(
          action?.payload.status,
          action.payload.trips,
          action.payload.work_order_issues,
        ),
        work_order_issues: workOrderIssues,
      };
      state.source = formattedData;

      // ORI-593 (QA): the open Issue Details panel holds a snapshot captured at open-time and is
      // never re-derived, so after a cancel + refetch it would keep showing stale data (no
      // cancellation reason) until reopened. Re-sync it from the freshly fetched issue.
      //
      // The gate is an OR over BOTH sides, because the panel has to track the cancellation
      // boundary in both directions:
      //  - ENTRY  (active -> cancelled): only the REFRESHED side is cancelled. The snapshot still
      //    holds its pre-cancel status — cancelVendorWorkOrder().fulfilled never patches `data` —
      //    so a snapshot-only gate would never pick the reason up.
      //  - EXIT   (cancelled -> reactivated): only the SNAPSHOT side is cancelled. A coordinator
      //    reschedule brings the issue back as `pending` (getWorkOrderIssueStatus maps
      //    assigned -> pending) on the live `work_order.rescheduled` Pusher refetch, and a
      //    refreshed-only gate would strand the dead cancellation block in the panel — keeping
      //    hideServiceReport true, so the vendor could never reach the new trip's Service Report
      //    without closing and reopening the panel.
      // Whether the reason actually RENDERS is the component's call (IssueDetails/index.tsx gates
      // it on status + decline origin); duplicating that presentation guard here is exactly what
      // stranded the exit path.
      //
      // It is deliberately NOT an unconditional sync: QuoteComponent pairs `data.quotes` with the
      // sibling `options`, so swapping the rendered branch under a mid-edit vendor (a half-filled
      // ServiceReportForm, an open quote task) on any unrelated background refetch would discard
      // their input. Both arms re-derive `data` AND `options` together through
      // buildIssueDetailsPanelSnapshot — patching `data` alone could land a `quoted` refreshed
      // issue next to the cancelled snapshot's stale option ids (the ORI-614 class).
      const openIssueId = state.sidePanels.issueDetails?.data?.issue_id;
      if (state.sidePanels.issueDetails?.isOpen && openIssueId) {
        const refreshedIssue = workOrderIssues.find((issue) => issue.issue_id === openIssueId);
        if (
          refreshedIssue &&
          (isCancelledLike(state.sidePanels.issueDetails.data?.status?.value) ||
            isCancelledLike(refreshedIssue.status?.value))
        ) {
          state.sidePanels.issueDetails = {
            ...state.sidePanels.issueDetails,
            ...buildIssueDetailsPanelSnapshot(refreshedIssue),
          };
        }
      }

      state.sidePanels.details = {
        ...state.sidePanels.details,
        areAllIssuesMarked: checkAllIssuesMarked(workOrderIssues),
        isLoading: false,
        isStatusLoading: false,
        needToRefetch: false,
        hasDetailsError: false,
      };
    },
  );
  builder.addCase(getWorkOrderDetails().rejected, (state: WorkOrderStateType, action) => {
    if ((action.payload as { actionType?: string })?.actionType === 'canceled') return;

    // ORI-627: this reducer no longer closes the panel. It used to set isOpen:false on
    // EVERY non-canceled rejection, which discarded the vendor's open work order — and
    // every nested sub-panel's in-progress input — on a one-shot 429/5xx/network blip
    // where the work order was still perfectly valid. Whether a rejection is fatal to the
    // panel is a *status* question (isEntityGoneRejection — 404/403/410), and only the
    // hook sees the error object; the hook also closes via updateUrlForCloseWoSidePanel(),
    // the one path that drops ?woId AND runs the ORI-465 nested cascade, neither of which
    // a pure reducer can do. So closure is the hook's, and this reducer's job shrank to
    // releasing the loading flags and raising the error the panel's Retry branch reads.
    const actionType = (action.payload as { actionType?: string } | undefined)?.actionType;

    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: false,
      isStatusLoading: false,
      needToRefetch: false,
      // The 401 sign-out shape is not a load failure — the api helper is already
      // redirecting to /login, so an error banner would flash over the redirect.
      // Same taxonomy as isCanceledError; `canceled` is redundant given the early
      // return above, but kept so this predicate greps identically to the coordinator's.
      hasDetailsError: actionType !== 'canceled' && actionType !== 'unauthorized',
    };
  });
  builder.addCase(cancelVendorWorkOrder().fulfilled, (state: WorkOrderStateType, action) => {
    // Destructure work_order_issues out of the payload before spreading so the
    // blanket `...payloadRest` spread below can never overwrite
    // state.source.work_order_issues with null/undefined if the API ever returns
    // the key explicitly. work_order_issues is managed exclusively via mergedIssues.
    const { work_order_issues: apiIssues, ...payloadRest } = action?.payload ?? {};

    // Compute the merged issues before building formattedData so the result can
    // be passed to getWorkOrderStatus (which uses issues to detect approved/rejected
    // quotes and may derive SCHEDULE_RETURN_TRIP or COMPLETED from PAUSED).
    // The cancel-trip API returns work_order_issues with only updated fields
    // (e.g. status, abilities) and omits others (e.g. description, media, quotes).
    // We iterate over existing state issues and patch API data on top so that:
    // - omitted fields (description, media, etc.) are preserved from state.
    // - issues absent from the API response are kept unchanged, guarding against
    //   the API returning only a subset of the work order's issues.
    // - if state has no issues yet, fall back to the API issues directly, normalised
    //   so that required fields like `quotes` always default to [] (other reducers
    //   access issue.quotes without optional chaining and would crash otherwise).
    const mergedIssues: WorkOrderIssueType[] | undefined = apiIssues
      ? state.source.work_order_issues?.length
        ? state.source.work_order_issues.map((existingIssue: WorkOrderIssueType) => {
            const apiIssue = apiIssues.find(
              (issue: WorkOrderIssueType) => issue.issue_id === existingIssue.issue_id,
            );
            if (!apiIssue) return existingIssue;
            const mergedIssue: WorkOrderIssueType = {
              ...existingIssue,
              ...apiIssue,
              quotes: apiIssue.quotes ?? existingIssue.quotes ?? [],
            };
            // ORI-653: the cancel-trip response carries the BARE wire title
            // (CancelTripResource -> `$issue->issue?->title`), so the `...apiIssue` spread
            // above overwrites the composed `(Repair)/(Replace)` suffix that
            // getWorkOrderDetails stored on `title`. Re-derive it from the merged issue and
            // the preserved `state.source.quotes`, exactly as getWorkOrderDetails.fulfilled
            // and scheduleVendorReturnTrip.fulfilled already do. Safe here because the cancel
            // payload omits `quote_task`, so the merged issue keeps the selected option the
            // work type resolves from, and the helper is idempotent (a bare title gains the
            // suffix, an already-suffixed one is unchanged). `base_title` is left untouched,
            // so the ORI-620 guard is unaffected.
            mergedIssue.title = getWorkOrderIssueTitle(mergedIssue, state.source.quotes);
            // The cancel-trip response omits `unresolved_from` (it only returns
            // status/abilities), so an issue this cancel just moved to `unresolved`
            // would carry no discriminator and the Issue Details guard would wrongly
            // show the recovery panel for a dead trip (ORI-144) until a reload. Stamp
            // `cancel_trip` ONLY on issues that transitioned to unresolved via this
            // cancel; issues already unresolved keep their existing reason (the API
            // returns every work-order issue, not just the cancelled trip's).
            if (
              mergedIssue.status?.value === ISSUES_STATUS.UNRESOLVED &&
              existingIssue.status?.value !== ISSUES_STATUS.UNRESOLVED
            ) {
              mergedIssue.unresolved_from = UNRESOLVE_TYPE.CANCEL_TRIP;
            }
            return mergedIssue;
          })
        : // Empty-state fallback: no existing issues to merge against, so the
          // "transitioned this cancel" predicate above cannot apply here. Deliberately do
          // NOT stamp cancel_trip — the cancel API omits `unresolved_from` for every issue
          // it returns, so a blanket stamp would mislabel a pre-existing non-cancel
          // unresolve (e.g. quality_check) and wrongly HIDE its recovery panel (ORI-481).
          // Leaving `unresolved_from` absent is the safe recover-forward default: the
          // IssueDetails guard treats a missing value as "not a cancel-trip" and renders
          // it ("A missing unresolved_from is never a cancel-trip" — IssueDetails/index.tsx).
          // This branch is effectively unreachable in the cancel flow anyway — a cancel
          // always fires from an already-loaded WO detail, so state has issues to merge.
          apiIssues.map((issue: WorkOrderIssueType) => ({
            ...issue,
            quotes: issue.quotes ?? [],
          }))
      : undefined;

    const formattedData: WorkOrderDataType = {
      ...state.source,
      ...payloadRest,
      ...(mergedIssues && { work_order_issues: mergedIssues }),
      // Guard against a missing/malformed status in the API response.
      // Validate status.value (not just status) because getWorkOrderStatus reads
      // status.value immediately and would throw if the API returns a status object
      // that is truthy but lacks the `value` field.
      // Pass trips and the effective issues so getWorkOrderStatus can correctly
      // derive SCHEDULE_RETURN_TRIP or COMPLETED when the status is PAUSED.
      // Fall back to state.source.work_order_issues when the API omits
      // work_order_issues entirely, so status derivation from existing quotes
      // stays consistent in the "no issues in payload" path.
      status: payloadRest?.status?.value
        ? getWorkOrderStatus(
            payloadRest.status,
            state.source.trips,
            mergedIssues ?? state.source.work_order_issues,
          )
        : state.source.status,
    };
    state.source = formattedData;
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: false,
      isStatusLoading: false,
      // ORI-593 (QA): a vendor cancel returns CancelTripResource, which omits the
      // cancellation-reason fields (they live on the work-order details resource). Refetch so
      // the reason appears in the open panel without the vendor manually reloading the browser.
      needToRefetch: true,
    };

    // ORI-504: a canceled work order should leave the current (open) list and
    // move tabs immediately, instead of only after a full reload. This is a
    // *trip* cancel (cancelVendorWorkOrder -> POST actions/cancelTrip), so the
    // resolved WO status determines the destination bucket — exactly like
    // closeWorkOrder branches its destination:
    //   - paused / schedule_return_trip  -> paused tab
    //   - otherwise (canceled/terminal)  -> closed tab
    // The count adjustment is gated on the row actually being removed from the
    // loaded list, which avoids a repeated/edge dispatch double-counting and
    // leaves counts untouched when the WO isn't in the current list (e.g. a
    // cancel from a detail-only context). The next getWorkOrders refetch
    // reconciles tab_counts from the server regardless.
    const canceledWoId = formattedData.work_order_id;
    if (Array.isArray(state.data) && state.tabCounts) {
      const beforeCount = state.data.length;
      state.data = state.data.filter((wo) => wo.work_order_id !== canceledWoId);

      if (state.data.length !== beforeCount) {
        if (state.tabCounts.open > 0) {
          state.tabCounts.open -= 1;
        }

        const resolvedStatus = formattedData.status?.value;
        if (
          resolvedStatus === WO_STATUS_KEYS.PAUSED ||
          resolvedStatus === WO_STATUS_KEYS.SCHEDULE_RETURN_TRIP
        ) {
          state.tabCounts.paused += 1;
        } else {
          state.tabCounts.closed += 1;
        }
      }
    }
  });

  // Work Order Status Updates
  // Scheduled -> En-route
  builder.addCase(
    enRouteWorkOrder().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.EN_ROUTE,
        label: WO_STATUS_LABELS.EN_ROUTE,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        en_route_at: trip.en_route_at,
        status: trip.status,
      });
    },
  );
  // En-route -> En-route Paused
  builder.addCase(
    pauseEnRouteWorkOrder().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.EN_ROUTE_PAUSED,
        label: WO_STATUS_LABELS.EN_ROUTE_PAUSED,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        en_route_paused_at: trip.en_route_paused_at,
        status: trip.status,
        total_drive_time_in_sec: trip.total_drive_time_in_sec,
      });
    },
  );
  builder.addCase(
    scheduleVendorReturnTrip().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...trip.status,
        value: WO_STATUS_KEYS.SCHEDULED,
        label: WO_STATUS_LABELS.SCHEDULED,
      };

      state.source.trips = addWorkOrderTrip(state.source.trips, trip);

      state.source.work_order_issues = state.source.work_order_issues.map(
        (issue: WorkOrderIssueType) => {
          // Find the quote task for this issue to get the selected option
          const quoteTask = state.source.quotes
            .flatMap((quote) => quote.tasks || [])
            .find((task) => task.work_order_issue_id === issue.issue_id);

          const updatedIssue = {
            ...issue,
            // ORI-602: this optimistic update prepends the new trip to `trips` (so
            // `trips[0]` is now the return trip) and re-attaches every issue to it,
            // mirroring the backend. `latest_trip_id` is BE-rendered and no other src
            // path maintains it, so keep it in sync here — otherwise the close-WO
            // Trip Cost Breakdown (which scopes by `latest_trip_id === trips[0].trip_id`)
            // would filter every issue out on the same-session return-trip close and
            // silently submit no materials.
            latest_trip_id: trip.trip_id,
            status: getWorkOrderIssueStatus(issue),
            // Preserve existing quote_task and only update selected_option.option_id
            ...(quoteTask?.selected_option
              ? {
                  quote_task: {
                    ...issue.quote_task,
                    selected_option: {
                      ...issue.quote_task?.selected_option,
                      option_id: quoteTask.selected_option,
                    },
                  },
                }
              : {}),
          };

          // Update title to include option type (Repair/Replace)
          return {
            ...updatedIssue,
            title: getWorkOrderIssueTitle(updatedIssue as WorkOrderIssueType, state.source.quotes),
          };
        },
      );
    },
  );
  // En-route Paused -> En-route
  builder.addCase(
    resumeEnRouteWorkOrder().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.EN_ROUTE,
        label: WO_STATUS_LABELS.EN_ROUTE,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        en_route_resumed_at: trip.en_route_resumed_at,
        status: trip.status,
        total_drive_time_in_sec: trip.total_drive_time_in_sec,
      });
    },
  );

  // En-route -> Work in Progress
  builder.addCase(
    startWorkInTrip().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.IN_PROGRESS,
        label: WO_STATUS_LABELS.IN_PROGRESS,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        status: trip.status,
        work_started_at: trip.work_started_at,
        total_drive_time_in_sec: trip.total_drive_time_in_sec,
      });
    },
  );

  // Work in Progress -> Work in Progress Paused
  builder.addCase(
    pauseWorkInTrip().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.IN_PROGRESS_PAUSED,
        label: WO_STATUS_LABELS.IN_PROGRESS_PAUSED,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        status: trip.status,
        work_paused_at: trip.work_paused_at,
        total_labor_time_in_sec: trip.total_labor_time_in_sec,
      });
    },
  );

  // Work in Progress Paused -> Work in Progress
  builder.addCase(
    resumeWorkInTrip().fulfilled,
    (state: WorkOrderStateType, action: PayloadAction<WorkOrderStatusChangeResponse>) => {
      const { trip } = action.payload;
      state.source.status = {
        ...action.payload.trip.status,
        value: WO_STATUS_KEYS.IN_PROGRESS,
        label: WO_STATUS_LABELS.IN_PROGRESS,
      };

      state.source.trips = updateWorkOrderTrip(state.source.trips, trip.trip_id, {
        status: trip.status,
        work_resumed_at: trip.work_resumed_at,
        total_labor_time_in_sec: trip.total_labor_time_in_sec,
      });
    },
  );

  // Quote Flow (Issue Status Changes)
  // Assigned -> Quoted
  builder.addCase(addQuote().pending, (state: WorkOrderStateType) => {
    state.sidePanels.quoteTaskDetails.isLoading = true;
  });
  builder.addCase(addQuote().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: false,
      isStatusLoading: false,
      needToRefetch: false,
    };
    // ORI-614: keep the panel OPEN on failure (matching addQuoteTask().rejected). The Offcanvas
    // unmounts its children on close, so closing here destroyed every option, photo and price the
    // vendor had entered — and this is now the primary path for a replacement quote.
    state.sidePanels.quoteTaskDetails.isLoading = false;
  });
  builder.addCase(
    addQuote().fulfilled,
    (state: WorkOrderStateType, action: { payload: QuoteDataResponseType }) => {
      const formattedData = formatQuoteResponseData(action.payload, state);

      state.source = formattedData;
      state.sidePanels.details = {
        ...state.sidePanels.details,
        areAllIssuesMarked: checkAllIssuesMarked(formattedData.work_order_issues),
        isLoading: false,
        isStatusLoading: false,
        needToRefetch: false,
      };
      state.sidePanels.quoteTaskDetails = {
        ...state.sidePanels.quoteTaskDetails,
        isLoading: false,
        isOpen: false,
      };
    },
  );

  builder.addCase(addQuoteTask().pending, (state: WorkOrderStateType) => {
    state.sidePanels.quoteTaskDetails.isLoading = true;
  });
  builder.addCase(addQuoteTask().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: false,
      isStatusLoading: false,
      needToRefetch: false,
    };
    state.sidePanels.quoteTaskDetails.isLoading = false;
  });
  builder.addCase(
    addQuoteTask().fulfilled,
    (state: WorkOrderStateType, action: { payload: QuoteTaskResponseType }) => {
      const formattedData: WorkOrderDataType = formatQuoteTaskResponseData(
        state.source,
        action.payload,
      );

      state.source = formattedData;
      state.sidePanels.details = {
        ...state.sidePanels.details,
        areAllIssuesMarked: checkAllIssuesMarked(formattedData.work_order_issues),
        isLoading: false,
        isStatusLoading: false,
        needToRefetch: false,
      };
      state.sidePanels.quoteTaskDetails = {
        ...state.sidePanels.quoteTaskDetails,
        isLoading: false,
        isOpen: false,
      };
    },
  );

  builder.addCase(updateQuoteTask().pending, (state: WorkOrderStateType) => {
    state.sidePanels.quoteTaskDetails.isLoading = true;
  });
  builder.addCase(updateQuoteTask().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.details = {
      ...state.sidePanels.details,
      isLoading: false,
      isStatusLoading: false,
      needToRefetch: false,
    };
    state.sidePanels.quoteTaskDetails.isLoading = false;
  });
  builder.addCase(
    updateQuoteTask().fulfilled,
    (state: WorkOrderStateType, action: { payload: QuoteTaskResponseType }) => {
      state.source = formatQuoteTaskResponseData(state.source, action.payload);
      state.sidePanels.details = {
        ...state.sidePanels.details,
        isLoading: false,
        isStatusLoading: false,
        needToRefetch: false,
      };
      state.sidePanels.quoteTaskDetails.isLoading = false;
      state.sidePanels.quoteTaskDetails.isOpen = false;
    },
  );

  // Quoted -> Pending
  builder.addCase(removeQuoteTask().pending, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = true;
  });
  builder.addCase(removeQuoteTask().fulfilled, (state: WorkOrderStateType, action) => {
    const { abilities, decline_reason: declineReason, issue_id: issueId } = action.payload;
    const status: StatusType = {
      color_class: '',
      label: ISSUES_STATUS_LABEL.PENDING,
      value: ISSUES_STATUS.PENDING,
    };
    const { allIssuesMarked, issues } = updateWorkOrderIssueStatus(
      state.source.work_order_issues,
      issueId,
      {
        abilities,
        decline_reason: declineReason,
        status,
      },
      status,
    );

    // ORI-614: operate on the quote that actually OWNS the removed issue's task rather than
    // quotes[0], falling back to the active quote. The old code filtered the rejected quote's tasks
    // and, worse, rebuilt `quotes` as a single-element array — deleting the replacement quote from
    // the store. Write back in place instead, and drop only the emptied quote (the pre-existing
    // single-quote case still yields `quotes: []`). With no target quote at all the write-back is a
    // no-op, which is correct — the request that produced this action cannot have removed anything.
    const quotes: QuoteType[] = state.source.quotes ?? [];
    const targetQuote: QuoteType | undefined =
      quotes.find(
        (quote: QuoteType) =>
          !isQuoteRejected(quote) &&
          quote.tasks?.some((task: QuoteTaskType) => task.work_order_issue_id === issueId),
      ) ?? getActiveWorkOrderQuote(quotes);
    const updatedQuoteTasks: QuoteTaskType[] = (targetQuote?.tasks ?? []).filter(
      (task: QuoteTaskType) => task.work_order_issue_id !== issueId,
    );

    const updatedQuotes: QuoteType[] =
      updatedQuoteTasks.length > 0
        ? quotes.map((quote: QuoteType) =>
            quote.quote_id === targetQuote?.quote_id
              ? { ...quote, tasks: updatedQuoteTasks }
              : quote,
          )
        : quotes.filter((quote: QuoteType) => quote.quote_id !== targetQuote?.quote_id);

    state.source = {
      ...state.source,
      work_order_issues: issues,
      quotes: updatedQuotes,
    };
    state.sidePanels.details = {
      ...state.sidePanels.details,
      areAllIssuesMarked: allIssuesMarked,
      isLoading: false,
      isStatusLoading: false,
      needToRefetch: false,
    };
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      isFormLoading: false,
      isOpen: false,
    };
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      isLoading: false,
      isOpen: false,
    };
  });
  builder.addCase(removeQuoteTask().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = false;
  });

  // Pending -> Unresolved
  builder.addCase(markAsUnresolve().pending, (state: WorkOrderStateType) => {
    state.sidePanels.markUnResolve.isFormLoading = true;
  });
  builder.addCase(
    markAsUnresolve().fulfilled,
    (
      state: WorkOrderStateType,
      action: PayloadAction<{
        issue_id: string;
        decline_reason: string;
        status: StatusType;
        abilities: string[];
      }>,
    ) => {
      const { abilities, decline_reason: declineReason, issue_id: issueId } = action.payload;

      const status: StatusType = {
        color_class: '',
        label: ISSUES_STATUS_LABEL.UNRESOLVED,
        value: ISSUES_STATUS.UNRESOLVED,
      };

      const { allIssuesMarked, issues } = updateWorkOrderIssueStatus(
        state.source.work_order_issues,
        issueId,
        {
          abilities,
          decline_reason: declineReason,
          status,
        },
        status,
      );

      state.source.work_order_issues = issues;
      state.sidePanels.details.areAllIssuesMarked = allIssuesMarked;
      state.sidePanels.markUnResolve = {
        ...state.sidePanels.markUnResolve,
        isFormLoading: false,
        isOpen: false,
      };
      state.sidePanels.issueDetails.isOpen = false;
    },
  );
  builder.addCase(markAsUnresolve().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.markUnResolve.isFormLoading = false;
  });

  builder.addCase(updateDeclineReason().pending, (state: WorkOrderStateType) => {
    state.sidePanels.markUnResolve.isFormLoading = true;
  });
  builder.addCase(
    updateDeclineReason().fulfilled,
    (
      state: WorkOrderStateType,
      action: PayloadAction<{
        issue_id: string;
        decline_reason: string;
        status: StatusType;
        abilities: string[];
      }>,
    ) => {
      const { abilities, decline_reason: declineReason, issue_id: issueId } = action.payload;

      const issues = updateWorkOrderIssue(state.source.work_order_issues, issueId, {
        abilities,
        decline_reason: declineReason,
      });

      state.source.work_order_issues = issues;
      state.sidePanels.markUnResolve = {
        ...state.sidePanels.markUnResolve,
        isFormLoading: false,
        isOpen: false,
      };
      state.sidePanels.issueDetails.isOpen = false;
    },
  );
  builder.addCase(updateDeclineReason().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.markUnResolve.isFormLoading = false;
  });

  // Unresolved -> Pending OR Done -> Pending
  builder.addCase(markAsPending().pending, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = true;
  });
  builder.addCase(markAsPending().fulfilled, (state: WorkOrderStateType, action: any) => {
    const { abilities, issue_id: issueId } = action.payload;

    const status: StatusType = {
      color_class: 'has-gray',
      label: ISSUES_STATUS_LABEL.PENDING,
      value: ISSUES_STATUS.PENDING,
    };

    const { allIssuesMarked, issues } = updateWorkOrderIssueStatus(
      state.source.work_order_issues,
      issueId,
      {
        abilities,
        decline_reason: '',
        issue_caused_details: '',
        materials: [],
        media: [],
        service_notes: '',
        status,
      },
      status,
    );

    state.source.work_order_issues = issues;
    state.sidePanels.details.areAllIssuesMarked = allIssuesMarked;
    state.sidePanels.markUnResolve = {
      ...state.sidePanels.markUnResolve,
      isFormLoading: false,
      isOpen: false,
    };
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      isLoading: false,
      isOpen: false,
    };
  });
  builder.addCase(markAsPending().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = false;
  });

  // Pending -> Done
  builder.addCase(markIssueAsDone().pending, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = true;
  });
  builder.addCase(markIssueAsDone().fulfilled, (state: WorkOrderStateType, action: any) => {
    const {
      abilities,
      issue_id: issueId,
      issue_caused_details: issueCausedDetails,
      materials,
      media,
      service_notes: serviceNotes,
      trip_issue_status: status,
    } = action.payload;

    const { allIssuesMarked, issues } = updateWorkOrderIssueStatus(
      state.source.work_order_issues,
      issueId,
      {
        abilities,
        issue_caused_details: issueCausedDetails,
        materials: resolveIssueMaterials(materials, action.payload.quote_task),
        media,
        service_notes: serviceNotes,
        status,
      },
      status,
    );

    state.source.work_order_issues = issues;
    state.sidePanels.details.areAllIssuesMarked = allIssuesMarked;
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      isLoading: false,
      isOpen: false,
    };
    state.sidePanels.quoteApprovedTaskDetails.isOpen = false;
  });
  builder.addCase(markIssueAsDone().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = false;
  });

  builder.addCase(saveServiceReportChanges().pending, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = true;
  });
  builder.addCase(saveServiceReportChanges().fulfilled, (state: WorkOrderStateType, action) => {
    const {
      abilities,
      issue_id: issueId,
      issue_caused_details: issueCausedDetails,
      materials,
      media,
      service_notes: serviceNotes,
      trip_issue_status: status,
    } = action.payload;

    const { allIssuesMarked, issues } = updateWorkOrderIssueStatus(
      state.source.work_order_issues,
      issueId,
      {
        abilities,
        issue_caused_details: issueCausedDetails,
        materials: resolveIssueMaterials(materials, action.payload.quote_task),
        media,
        service_notes: serviceNotes,
        status,
      },
      status,
    );

    // TEMPORARY FE-ONLY FIX FOR DEMO — revisit this.
    // Resubmitting the last outstanding "missing info" issue moves the whole
    // batch back into quality check server-side, but the API only reports this
    // issue's own new status — promote sibling `done` issues locally too so the
    // UI doesn't need a refresh to reflect it. Only issues currently at `done`
    // are promoted; a WO with a mix of `quoted`/`unresolved` issues alongside
    // missing-info ones is unverified — confirm with backend/QA whether those
    // should also flip together, and whether the backend should just tell the
    // FE which issues changed instead of this being inferred client-side.
    let updatedIssues = issues;
    if (status.value === ISSUES_STATUS.QUALITY_CHECK) {
      updatedIssues = issues.map((issue) => {
        const isSiblingDoneIssue =
          issue.issue_id !== issueId && issue.status.value === ISSUES_STATUS.DONE;
        if (!isSiblingDoneIssue) {
          return issue;
        }
        return {
          ...issue,
          status: {
            value: ISSUES_STATUS.QUALITY_CHECK,
            label: ISSUES_STATUS_LABEL_MAP[ISSUES_STATUS.QUALITY_CHECK],
          },
        };
      });
    }
    state.source.work_order_issues = updatedIssues;
    // TEMPORARY FE-ONLY FIX FOR DEMO — revisit  this.
    // Same temporary fix — the WO itself also re-enters quality check alongside
    // its issues. Reuses `trip_issue_status` (already the correctly-shaped
    // `{value, label, color_class}` status object per the WO's own status
    // field) rather than the differently-shaped per-issue `status`.
    if (status.value === ISSUES_STATUS.QUALITY_CHECK) {
      state.source.status = status;
    }
    state.sidePanels.details.areAllIssuesMarked = allIssuesMarked;
    state.sidePanels.issueDetails = {
      ...state.sidePanels.issueDetails,
      isLoading: false,
    };
  });
  builder.addCase(saveServiceReportChanges().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.issueDetails.isLoading = false;
  });

  // En-route -> Scheduled OR Work in Progress -> Scheduled
  builder.addCase(rescheduleVendorWorkOrder().pending, (state: WorkOrderStateType) => {
    state.sidePanels.details.isStatusLoading = true;
    state.sidePanels.reschedule.isFormLoading = true;
  });
  builder.addCase(rescheduleVendorWorkOrder().fulfilled, (state: WorkOrderStateType, action) => {
    state.sidePanels.details.isStatusLoading = false;
    state.sidePanels.reschedule.isFormLoading = false;
    let { status } = state.source;
    if (action.payload.trip.status.value === 're_scheduled') {
      state.source.trips = addWorkOrderTrip(state.source.trips, action.payload.trip);
      status = {
        color_class: '',
        label: WO_STATUS_LABELS.SCHEDULED,
        value: WO_STATUS_KEYS.SCHEDULED,
      };
    } else if (status.value === WO_STATUS_KEYS.EN_ROUTE) {
      status = {
        color_class: '',
        label: WO_STATUS_LABELS.EN_ROUTE_PAUSED,
        value: WO_STATUS_KEYS.EN_ROUTE_PAUSED,
      };
    } else if (status.value === WO_STATUS_KEYS.IN_PROGRESS) {
      status = {
        color_class: '',
        label: WO_STATUS_LABELS.IN_PROGRESS_PAUSED,
        value: WO_STATUS_KEYS.IN_PROGRESS_PAUSED,
      };
    }

    state.source.status = status;
  });
  builder.addCase(rescheduleVendorWorkOrder().rejected, (state: WorkOrderStateType) => {
    state.sidePanels.details.isStatusLoading = false;
    state.sidePanels.reschedule.isFormLoading = false;
  });

  // Work in Progress -> Quality Check OR Work in Progress -> Paused
  builder.addCase(closeWorkOrder().fulfilled, (state: WorkOrderStateType, action) => {
    const {
      abilities,
      status,
      trip,
      work_order_id: woId,
      total_invoice_amount_in_cents,
    } = action.payload;
    const quoteReason = action.payload.quote_reason || action.meta.arg.reason;
    // The close transition is what stamps the quote's submitted_at, so this response is the
    // only place this tab learns it — the `quote.status_updated` broadcast that used to carry
    // it is now excluded from the acting socket. Without it the Quotes tab card renders its
    // 'Invalid date' fallback until a refetch. Older backends omit the key; keep the stored
    // value in that case rather than blanking it.
    const quoteSubmittedAt: string | undefined = action.payload.quote_submitted_at;

    // Draft -> Pending Review, applied locally because the vendor `quote.status_updated`
    // broadcast that used to carry it is now excluded from the socket that made this request.
    // `state` moves with `status`: the echo set both, and helpers such as isQuoteRejected read
    // whichever is present, so leaving `state` on 'draft' makes the two disagree.
    const applySubmissionToActiveQuote = (quote: QuoteType): QuoteType => {
      if (!quote?.is_active) {
        return quote;
      }
      return {
        ...quote,
        quote_reason: quoteReason,
        submitted_at: quoteSubmittedAt ?? quote.submitted_at,
        state: QUOTE_STATUS_KEYS.PENDING_REVIEW,
        status: {
          label: 'Pending Review',
          value: QUOTE_STATUS_KEYS.PENDING_REVIEW,
          color_class: 'has-warning-light',
        },
      };
    };

    state.sidePanels.closeWorkOrder.currentTab = WO_CLOSE_WORK_ORDER_TABS_KEYS.CLOSED;

    state.tabCounts.open -= 1;

    if (status.value === WO_STATUS_KEYS.QUALITY_CHECK) {
      state.tabCounts.closed += 1;
    } else if (status.value === WO_STATUS_KEYS.PAUSED) {
      state.tabCounts.paused += 1;
    }

    // The closeWorkOrder API has no typed response contract, so the payload may
    // omit `total_invoice_amount_in_cents` entirely. When it does, preserve the
    // previously-loaded value rather than overwriting it with `undefined`,
    // which would otherwise force the Trip Summary derivation onto its
    // materials-fallback branch on a WO that already had a finalised invoice.
    const nextInvoiceTotal =
      total_invoice_amount_in_cents === undefined
        ? state.source.total_invoice_amount_in_cents
        : total_invoice_amount_in_cents;

    // ORI-146 (comment df10c872 — "Missing Labor / Trip Fee"). The close-WO
    // response payload does not echo back the WO-level Labor / Trip Fee the
    // vendor entered, so we capture it from the request metadata and persist
    // it on the source so the Trip Summary derivation can surface it even
    // when `total_invoice_amount_in_cents` has not been finalised yet. The
    // wire key on `meta.arg` remains `labor_in_cent` (owned by the close-WO
    // panel and the backend POST contract); the stored field on
    // `state.source` is `labor_cost_in_cents`. When the wire key is absent
    // from the request (e.g. test fixtures or a future close-out variant
    // that omits it), preserve the existing stored value rather than
    // overwriting it with `undefined`.
    const submittedLaborInCent = (action.meta.arg as { labor_in_cent?: unknown } | undefined)
      ?.labor_in_cent;
    const coercedLaborInCent = Number(submittedLaborInCent);
    const nextLaborInCent =
      submittedLaborInCent === undefined || !Number.isFinite(coercedLaborInCent)
        ? state.source.labor_cost_in_cents
        : coercedLaborInCent;

    // The closeWorkOrder API has no typed response contract, so the payload may
    // omit `trip` (or `trip.trip_id`) entirely. When it does, preserve the
    // existing trips array rather than dereferencing an undefined object,
    // which would otherwise throw inside the `state.source = { ... }`
    // assignment and leave the vendor WO store in a half-applied draft.
    const nextTrips =
      trip?.trip_id === undefined
        ? state.source.trips
        : updateWorkOrderTrip(state.source.trips, trip.trip_id, {
            status: trip.status,
            work_started_at: trip.work_started_at,
            work_paused_at: trip.work_paused_at,
            work_resumed_at: trip.work_resumed_at,
            total_labor_time_in_sec: trip.total_labor_time_in_sec,
            total_drive_time_in_sec: trip.total_drive_time_in_sec,
          });

    state.source = {
      ...state.source,
      abilities,
      status,
      total_invoice_amount_in_cents: nextInvoiceTotal,
      labor_cost_in_cents: nextLaborInCent,
      trips: nextTrips,
      quotes: (state.source.quotes ?? []).map(applySubmissionToActiveQuote),
      // The nested issue-level copy of the quote must move too. Both copies used to be
      // reconciled by the `quote.status_updated` echo (updateWorkOrderQuotes writes the
      // work-order list AND work_order_issues[].quotes), but that echo no longer reaches the
      // tab that closed the WO, so the Issue Details pane would keep rendering a Draft quote.
      work_order_issues: (state.source.work_order_issues ?? []).map((issue: WorkOrderIssueType) =>
        issue?.quotes?.length
          ? { ...issue, quotes: issue.quotes.map(applySubmissionToActiveQuote) }
          : issue,
      ),
    };

    state.data = state.data.filter((wo) => wo.work_order_id !== woId);
  });
};

export default extraReducers;
